import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { Input } from "@nosible/shared/components/ui/input";
import { Textarea } from "@nosible/shared/components/ui/textarea";
import { Tag, Pencil, Check, X, Loader2 } from "lucide-react";
import { trpc } from "@/admin/lib/trpc";

interface SubNameDescProps {
  subscriptionId: string;
  currentName: string;
  currentDescription: string | null;
  onSuccess?: () => void;
}

type EditingField = "name" | "description" | null;

export function SubNameDesc({
  subscriptionId,
  currentName,
  currentDescription,
  onSuccess,
}: SubNameDescProps) {
  const [editing, setEditing] = useState<EditingField>(null);
  const [nameValue, setNameValue] = useState(currentName);
  const [descValue, setDescValue] = useState(currentDescription ?? "");

  const nameMutation = trpc.admin.updateSubscriptionName.useMutation({
    onSuccess: () => {
      setEditing(null);
      onSuccess?.();
    },
    onError: () => {
      setNameValue(currentName);
      setEditing(null);
    },
  });

  const descMutation = trpc.admin.updateSubscriptionDescription.useMutation({
    onSuccess: () => {
      setEditing(null);
      onSuccess?.();
    },
    onError: () => {
      setDescValue(currentDescription ?? "");
      setEditing(null);
    },
  });

  const handleSaveName = () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === currentName) {
      setEditing(null);
      setNameValue(currentName);
      return;
    }
    nameMutation.mutate({ subscriptionId, name: trimmed });
  };

  const handleSaveDesc = () => {
    const trimmed = descValue.trim();
    const normalized = trimmed || null;
    if (normalized === (currentDescription ?? null)) {
      setEditing(null);
      return;
    }
    descMutation.mutate({ subscriptionId, description: normalized });
  };

  const handleCancelName = () => {
    setNameValue(currentName);
    setEditing(null);
  };

  const handleCancelDesc = () => {
    setDescValue(currentDescription ?? "");
    setEditing(null);
  };

  const isPending = nameMutation.isPending || descMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Subscription Identity
            </CardTitle>
            <CardDescription>Name and description for this subscription</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Name */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-chromium-mist">Name</p>
            {editing !== "name" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing("name")}
                disabled={isPending}
              >
                <Pencil className="h-3 w-3 mr-1" />
                Edit
              </Button>
            )}
          </div>
          {editing === "name" ? (
            <div className="flex items-center gap-2">
              <Input
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveName();
                  if (e.key === "Escape") handleCancelName();
                }}
                maxLength={100}
                autoFocus
                className="flex-1"
                disabled={nameMutation.isPending}
              />
              <Button
                size="sm"
                onClick={handleSaveName}
                disabled={nameMutation.isPending || !nameValue.trim()}
              >
                {nameMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Check className="h-4 w-4" />
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancelName}
                disabled={nameMutation.isPending}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <p className="text-silica font-medium">{currentName}</p>
          )}
          {nameMutation.isError && (
            <p className="text-red-400 text-sm mt-1">{nameMutation.error.message}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm text-chromium-mist">Description</p>
            {editing !== "description" && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing("description")}
                disabled={isPending}
              >
                <Pencil className="h-3 w-3 mr-1" />
                Edit
              </Button>
            )}
          </div>
          {editing === "description" ? (
            <div className="space-y-2">
              <Textarea
                value={descValue}
                onChange={(e) => setDescValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") handleCancelDesc();
                }}
                maxLength={500}
                autoFocus
                rows={3}
                disabled={descMutation.isPending}
                placeholder="Optional description…"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleSaveDesc}
                  disabled={descMutation.isPending}
                >
                  {descMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancelDesc}
                  disabled={descMutation.isPending}
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-silica">
              {currentDescription || <span className="text-chromium-mist/60 italic">No description</span>}
            </p>
          )}
          {descMutation.isError && (
            <p className="text-red-400 text-sm mt-1">{descMutation.error.message}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
